<script setup lang="ts">
import { computed, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import { notify } from '@/services/notice';
import type { FmeaNode, FmeaTreeResponse } from '@/types/domain';
import { errorMessage } from '@/utils/format';
import { confirmAction } from '@/services/confirm';

const props = defineProps<{
  projectId: number | string;
  documentId: number | string;
  subject: string;
  /** 当前选中的功能节点；没选功能就不能生成，失效链必须挂在功能下 */
  targetFunction: FmeaNode | null;
  /** 该功能所属的过程步骤名，作为生成上下文 */
  processName: string;
  workElements: Array<{ type: string; description: string }>;
}>();
const emit = defineEmits<{ adopted: [tree: FmeaTreeResponse] }>();

interface Chain {
  effect?: string;
  mode?: string;
  severity?: number | null;
  occurrence?: number | null;
  detection?: number | null;
  actionPriority?: string | null;
  causes?: Array<string | { description?: string; workElementType?: string }>;
  actions?: Array<{ description?: string; type?: string; isControl?: boolean }>;
}

const generating = ref(false);
const adopting = ref(false);
const chains = ref<Chain[]>([]);
const warnings = ref<string[]>([]);
const selected = ref<Set<number>>(new Set());
const aiReady = ref<boolean | null>(null);

async function checkAi() {
  try {
    const status = await eqsApi.fmeaAiStatus(props.projectId);
    aiReady.value = status.configured;
  } catch {
    aiReady.value = false;
  }
}
void checkAi();

async function generate() {
  if (!props.targetFunction) {
    notify('error', '请先选择一个功能', '失效链必须挂在「功能」节点下，请在结构树中点选一个功能。');
    return;
  }
  generating.value = true;
  chains.value = [];
  warnings.value = [];
  try {
    const result = await eqsApi.generateFmeaFailures(props.projectId, {
      subject: props.subject,
      processName: props.processName,
      functionDescription: props.targetFunction.title,
      workElements: props.workElements,
    });
    chains.value = (result.proposal ?? []) as Chain[];
    warnings.value = result.warnings ?? [];
    // 默认全选：多数情况下工程师是全要的，个别不合适的再取消
    selected.value = new Set(chains.value.map((_, index) => index));
    notify('success', `AI 生成了 ${chains.value.length} 条失效链`, '请逐条确认后再采纳。');
  } catch (caught) {
    notify('error', 'AI 生成失败', errorMessage(caught));
  } finally {
    generating.value = false;
  }
}

function toggle(index: number) {
  const next = new Set(selected.value);
  if (next.has(index)) next.delete(index);
  else next.add(index);
  selected.value = next;
}

const chosen = computed(() => chains.value.filter((_, index) => selected.value.has(index)));

async function adopt() {
  if (!props.targetFunction || !chosen.value.length) return;
  if (!await confirmAction({
    title: `确认把选中的 ${chosen.value.length} 条失效链写入「${props.targetFunction.title}」？`,
    message: '写入后可在结构树中继续修改或删除。',
  })) return;
  adopting.value = true;
  try {
    const tree = await eqsApi.adoptFmeaChains(props.projectId, props.documentId, {
      functionId: props.targetFunction.id,
      chains: chosen.value,
    });
    notify('success', `已采纳 ${tree.adopted ?? 0} 个条目`, '可在结构树中查看与调整。');
    chains.value = [];
    selected.value = new Set();
    emit('adopted', tree);
  } catch (caught) {
    notify('error', '采纳失败', errorMessage(caught));
  } finally {
    adopting.value = false;
  }
}

function causeText(cause: string | { description?: string; workElementType?: string }): string {
  if (typeof cause === 'string') return cause;
  const prefix = cause.workElementType ? `[${cause.workElementType}] ` : '';
  return `${prefix}${cause.description ?? ''}`;
}
</script>

<template>
  <section class="ai-panel">
    <header>
      <div>
        <span>AI / FAILURE CHAIN</span>
        <h4>AI 生成失效链</h4>
      </div>
      <button
        class="button button-primary"
        type="button"
        :disabled="generating || aiReady === false"
        @click="generate"
      >{{ generating ? '生成中…（约 30-60 秒）' : 'AI 生成' }}</button>
    </header>

    <p v-if="aiReady === false" class="ai-note warn">
      尚未配置 AI 密钥，生成功能不可用。请在服务器 <code>/home/eqs-next/ai.env</code> 中填写后重新部署。
    </p>
    <p v-else-if="!targetFunction" class="ai-note">
      请先在结构树中点选一个<b>功能</b>节点 —— 失效链只能挂在功能下。
    </p>
    <p v-else class="ai-note">
      将为功能「<b>{{ targetFunction.title }}</b>」生成失效链，上下文：{{ processName || '未指定工序' }}
      · 工作要素 {{ workElements.length }} 项。
    </p>

    <ul v-if="warnings.length" class="ai-warnings">
      <li v-for="(text, index) in warnings" :key="index">{{ text }}</li>
    </ul>

    <div v-if="chains.length" class="ai-chains">
      <div class="ai-chains-head">
        <b>共 {{ chains.length }} 条建议，已选 {{ chosen.length }} 条</b>
        <span>AI 生成内容仅供参考，采纳前请逐条确认 —— 采纳后即成为正式 FMEA 记录。</span>
      </div>

      <article v-for="(chain, index) in chains" :key="index" :class="{ picked: selected.has(index) }">
        <label class="ai-pick">
          <input type="checkbox" :checked="selected.has(index)" @change="toggle(index)">
          <b v-if="chain.actionPriority" class="ai-ap" :class="`ap-${String(chain.actionPriority).toLowerCase()}`">
            AP {{ chain.actionPriority }}
          </b>
          <em>S{{ chain.severity ?? '-' }} / O{{ chain.occurrence ?? '-' }} / D{{ chain.detection ?? '-' }}</em>
        </label>
        <dl>
          <dt>影响</dt><dd>{{ chain.effect || '—' }}</dd>
          <dt>模式</dt><dd>{{ chain.mode || '—' }}</dd>
          <dt>原因</dt>
          <dd>
            <span v-for="(cause, ci) in chain.causes || []" :key="ci" class="ai-sub">{{ causeText(cause) }}</span>
            <span v-if="!(chain.causes || []).length">—</span>
          </dd>
          <dt>措施</dt>
          <dd>
            <span v-for="(action, ai2) in chain.actions || []" :key="ai2" class="ai-sub">
              [{{ action.type === 'detection' ? '探测' : '预防' }}·{{ action.isControl ? '现有' : '建议' }}]
              {{ action.description }}
            </span>
            <span v-if="!(chain.actions || []).length">—</span>
          </dd>
        </dl>
      </article>

      <footer>
        <button
          class="button button-primary"
          type="button"
          :disabled="adopting || !chosen.length"
          @click="adopt"
        >{{ adopting ? '写入中…' : `采纳选中的 ${chosen.length} 条` }}</button>
        <button class="button" type="button" :disabled="adopting" @click="chains = []">放弃</button>
      </footer>
    </div>
  </section>
</template>

<style scoped>
.ai-panel { display: grid; gap: 8px; padding: 12px 14px; margin-bottom: 12px; border: 1px solid var(--line); border-left: 3px solid var(--blue); background: var(--paper); }
.ai-panel > header { display: flex; align-items: flex-end; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.ai-panel header span { color: var(--muted); font: 700 10px var(--display); letter-spacing: .08em; }
.ai-panel header h4 { margin: 2px 0 0; font-size: 14px; }
.ai-note { margin: 0; color: var(--muted); font-size: 12px; line-height: 1.7; }
.ai-note.warn { color: #a35b12; }
.ai-note code { padding: 0 3px; background: var(--white); font-size: 12px; }
.ai-warnings { margin: 0; padding: 8px 10px 8px 26px; border-left: 3px solid #d98324; background: #fdf6ec; color: #7a4a10; font-size: 12px; line-height: 1.7; }
.ai-chains { display: grid; gap: 8px; }
.ai-chains-head { display: grid; gap: 2px; }
.ai-chains-head b { font-size: 12px; }
.ai-chains-head span { color: #a35b12; font-size: 12px; }
.ai-chains article { padding: 8px 10px; border: 1px solid var(--line); background: var(--white); opacity: .55; }
.ai-chains article.picked { opacity: 1; border-color: var(--blue); }
.ai-pick { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; cursor: pointer; }
.ai-pick em { color: var(--muted); font-size: 12px; font-style: normal; }
.ai-ap { padding: 0 5px; border-radius: 3px; font-size: 12px; font-weight: 800; }
.ai-ap.ap-h { background: #fdecea; color: #922; }
.ai-ap.ap-m { background: #fdf3e3; color: #a35b12; }
.ai-ap.ap-l { background: #eef7ec; color: #2b6b32; }
.ai-chains dl { display: grid; grid-template-columns: 40px 1fr; gap: 3px 8px; margin: 0; font-size: 12px; }
.ai-chains dt { color: var(--muted); }
.ai-chains dd { margin: 0; color: var(--ink); line-height: 1.6; }
.ai-sub { display: block; }
.ai-chains footer { display: flex; gap: 8px; }
</style>
